import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { onQuotaExceeded } from '../../lib/billing';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export function QuotaExceededDialog() {
  const [detail, setDetail] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [showPayId, setShowPayId] = useState(false);
  const [payId, setPayId] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onQuotaExceeded((d) => setDetail(d));
    return () => unsub();
  }, []);

  if (!detail) return null;

  const code = detail.code || 'quota_exceeded';
  const feature = (detail.feature || 'feature').toUpperCase();
  const limit = detail.limit;
  const used = detail.used;
  const planLabel = detail.plan || 'your plan';
  const message = detail.message || 'You have reached the limit for this feature.';
  const recommendUpgrade = code === 'plan_forbidden' || (limit !== null && used !== undefined && used >= (limit || 0));
  // Show the reconcile-payment escape hatch when the user hits a plan wall.
  // Handles the case where someone paid via the Razorpay hosted button but
  // the webhook auto-provisioned a *separate* account (different email at
  // checkout) so the current session still shows as unpaid.
  const showReconcile = code === 'plan_forbidden';

  const onClose = () => {
    setDetail(null);
    setShowPayId(false);
    setPayId('');
  };
  const onUpgrade = () => { onClose(); navigate('/pricing'); };

  const runReconcile = async (payment_id) => {
    setReconciling(true);
    try {
      const res = await axios.post(
        `${BACKEND}/api/billing/reconcile`,
        payment_id ? { razorpay_payment_id: payment_id.trim() } : {},
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' }, timeout: 25000 },
      );
      const data = res?.data || {};
      if (data.activated || data.already_active) {
        toast.success(data.message || 'Your plan is now active.');
        window.dispatchEvent(new Event('billing:refresh'));
        onClose();
        // gentle reload so gated flows unlock without extra clicks
        setTimeout(() => { try { window.location.reload(); } catch (_) { /* ignore */ } }, 500);
      } else {
        toast.error(data.message || 'Could not find a payment for your account.');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.message || err?.message;
      toast.error(typeof msg === 'string' ? msg : 'Reconcile failed. Please contact help@blayzrr.com.');
    } finally {
      setReconciling(false);
    }
  };

  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="quota-exceeded-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-[hsl(var(--warning))]" />
            {code === 'plan_forbidden' ? 'Not in your plan' : 'Quota reached'}
          </DialogTitle>
          <DialogDescription className="font-mono-edge text-[11px] text-muted-foreground">
            CODE {(code || '402').toUpperCase()} · FEATURE {feature}
            {typeof limit === 'number' && ` \u00b7 LIMIT ${limit}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm">{message}</p>
          {typeof limit === 'number' && typeof used === 'number' && limit > 0 && (
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-[hsl(var(--destructive))]"
                style={{ width: `${Math.min(100, Math.round((used / limit) * 100))}%` }}
              />
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Plan: <span className="font-medium text-foreground">{planLabel}</span>
          </p>

          {showReconcile && (
            <div className="mt-3 rounded-xl border border-dashed border-border/70 bg-secondary/40 p-3">
              <p className="text-[13px] font-medium">Already paid via Razorpay?</p>
              <p className="text-[12px] text-muted-foreground mt-1">
                Sometimes the webhook takes a moment. Click below and we&apos;ll
                find your payment and activate your plan.
                <span className="block mt-1">
                  Works best when this account&apos;s email matches the{' '}
                  <span className="font-semibold text-foreground">same email you paid with</span>{' '}
                  on Razorpay. If they differ, paste your Razorpay payment id below and
                  we&apos;ll match by id instead.
                </span>
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => runReconcile()}
                  disabled={reconciling}
                  data-testid="quota-modal-reconcile-button"
                  className="rounded-full"
                >
                  {reconciling ? (
                    <><Loader2 size={13} className="mr-1.5 animate-spin" /> Checking…</>
                  ) : (
                    <><RefreshCw size={13} className="mr-1.5" /> I already paid</>
                  )}
                </Button>
                {!showPayId ? (
                  <button
                    type="button"
                    onClick={() => setShowPayId(true)}
                    className="text-[12px] text-primary hover:underline"
                    data-testid="quota-modal-reconcile-toggle-payid"
                  >
                    Have a Razorpay payment id?
                  </button>
                ) : (
                  <div className="w-full mt-1">
                    <Label htmlFor="reconcile-payid" className="text-[11px] font-mono-edge tracking-[0.18em] uppercase text-muted-foreground">
                      Razorpay payment id
                    </Label>
                    <div className="mt-1 flex gap-2">
                      <Input
                        id="reconcile-payid"
                        placeholder="pay_XXXXXXXXXXXXXX"
                        value={payId}
                        onChange={(e) => setPayId(e.target.value)}
                        className="h-9 rounded-lg font-mono text-[13px]"
                        data-testid="quota-modal-reconcile-payid-input"
                      />
                      <Button
                        size="sm"
                        onClick={() => payId && runReconcile(payId)}
                        disabled={!payId || reconciling}
                        data-testid="quota-modal-reconcile-payid-submit"
                        className="rounded-lg"
                      >
                        {reconciling ? <Loader2 size={13} className="animate-spin" /> : 'Reconcile'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="secondary" onClick={onClose} data-testid="quota-modal-dismiss-button">
            Not now
          </Button>
          {recommendUpgrade && (
            <Button onClick={onUpgrade} data-testid="quota-modal-upgrade-button">
              Upgrade <ArrowUpRight size={14} className="ml-1" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default QuotaExceededDialog;
